import type { DataLayerConversionEvent } from '../analytics/formDetection/strategies/DataLayerStrategy';
import type { FormTrackingData } from '../analytics/formDetection/types';
import type { QualificationProfilingFormsValue } from '../types/configs.generated';
import { resolveDynamicQuestionsForUrl } from '../utils/content/dynamicQuestionResolution';
import { findBestLocaleValue, normalizeLocaleRecord } from '../utils/content/localeUtils';
import { logger } from '../utils/core/logger';
import { isMobileDevice } from '../utils/device/mobileDetection';
import { resolveBookingProvider } from '../utils/web/bookingProviders';
import {
    type CtaParametersForwardingMode,
    DEFAULT_CTA_PARAMETERS_FORWARDING_ALLOWLIST,
} from '../utils/web/ctaParameterForwarding';
import { isSpaHandlingEnabledForConfig } from '../utils/web/spaHandling';
import { matchesUrlPattern } from '../utils/web/urlUtils';
import { getCtaAliasOverride, resolveCtaLocaleValue } from './ctaResolution';
import {
    getClientPostConversionActivation,
    isFormSubmitActivated,
} from './PostConversionFormSubmitTracker';
import type {
    AgentDefinition,
    CTA,
    DynamicQuestionsConfig,
    PageDisplayRule,
    ParentWindowEventsSettings,
} from './types';
import type { ConfigResolver } from './unified/ConfigResolver';

/**
 * Helper function to extract the main domain from a hostname.
 * Example: "docs.example.com" -> "example.com"
 */
export function extractMainDomain(domain: string): string {
    const parts = domain.split('.');

    if (parts.length === 1) {
        return domain;
    }

    if (parts.length >= 3) {
        const lastPart = parts[parts.length - 1];
        const secondLastPart = parts[parts.length - 2];
        const countryCodeTLDs = ['co', 'com', 'net', 'org', 'gov', 'edu', 'ac'];
        const countryTLDs = ['uk', 'au', 'jp', 'br', 'in', 'za', 'nz', 'mx', 'ar', 'cl'];

        if (countryCodeTLDs.includes(secondLastPart) && countryTLDs.includes(lastPart)) {
            return parts.slice(-3).join('.');
        }
    }

    return parts.slice(-2).join('.');
}

let globalSiteConfigContext: {
    currentResolver: ConfigResolver | null;
    getResolver: (domain: string) => ConfigResolver | undefined;
    isSiteSupported: (domain: string) => boolean;
    isCurrentURLAllowed: (domain: string) => boolean;
    isLoaded: boolean;
} | null = null;

export function setSiteConfigContext(context: typeof globalSiteConfigContext): void {
    globalSiteConfigContext = context;
}

function getContext() {
    return globalSiteConfigContext;
}

function getCurrentResolver(): ConfigResolver | null {
    const context = getContext();
    if (!context?.isLoaded) {
        return null;
    }
    return context.currentResolver;
}

export function getCurrentConfigResolver(): ConfigResolver | null {
    return getCurrentResolver();
}

export function getResolverForDomain(domain: string): ConfigResolver | null {
    const context = getContext();
    if (!context?.isLoaded) {
        logger.debug('Config resolver context not available or not loaded yet');
        return null;
    }
    return context.getResolver(domain) ?? null;
}

function transformCtas(resolver: ConfigResolver): CTA[] {
    const items = resolver.ctas?.ctas;
    if (!items || items.length === 0) {
        return [];
    }

    return items.map((item) => {
        const langs: Record<string, { url: string; text: string }> = {};
        const normalizedUrls = normalizeLocaleRecord(item.url);
        const normalizedTexts = normalizeLocaleRecord(item.text);
        const aliasOverride = resolver.isAlias
            ? getCtaAliasOverride(resolver.ctas?.alias_overrides, resolver.domain, item.cta_id)
            : undefined;
        const normalizedAliasUrls = normalizeLocaleRecord(aliasOverride?.url);
        const normalizedAliasTexts = normalizeLocaleRecord(aliasOverride?.text);
        const effectiveUrls = resolver.isAlias ? normalizedAliasUrls : normalizedUrls;
        const effectiveTexts = resolver.isAlias ? normalizedAliasTexts : normalizedTexts;
        const allLangs = new Set(
            resolver.isAlias
                ? [...Object.keys(normalizedAliasUrls), ...Object.keys(normalizedAliasTexts)]
                : [...Object.keys(normalizedUrls), ...Object.keys(normalizedTexts)]
        );

        for (const lang of allLangs) {
            langs[lang] = {
                url: resolveCtaLocaleValue(effectiveUrls, lang) ?? '',
                text:
                    resolveCtaLocaleValue(effectiveTexts, lang) ??
                    (resolver.isAlias ? '' : item.cta_id),
            };
        }

        return {
            placeholder: item.cta_id,
            cta_id: item.cta_id,
            langs,
            type: 'regular',
            post_booking_redirect_url: item.post_booking_redirect_url,
            show_on_intents: item.show_on_intents,
        };
    });
}

function getDynamicQuestionsConfig(
    domain: string,
    language: string
): string[] | DynamicQuestionsConfig | null {
    const dq = getResolverForDomain(domain)?.engagement?.dynamic_questions;
    if (dq?.enabled === false) return null;
    return findBestLocaleValue(dq?.questions, language) ?? null;
}

function getParentWindowEventsSettings(domain: string): ParentWindowEventsSettings | null {
    return getResolverForDomain(domain)?.analytics.parent_window_events ?? null;
}

export function getSiteNameFromContext(domain: string): string | undefined {
    return getResolverForDomain(domain)?.identity.company_name;
}

export function isSiteSupported(domain: string): boolean {
    const context = getContext();
    if (!context?.isLoaded) {
        return false;
    }
    return context.isSiteSupported(domain);
}

export function isCurrentURLAllowed(domain: string): boolean {
    const context = getContext();
    if (!context?.isLoaded) {
        return true;
    }
    return context.isCurrentURLAllowed(domain);
}

export function getSiteColor(domain: string): string {
    return getResolverForDomain(domain)?.appearance.brand_color ?? '#0A42C3';
}

export function getDefaultLanguage(domain: string): string | undefined {
    return getResolverForDomain(domain)?.identity.default_language ?? undefined;
}

/**
 * Whether the domain is an alias of another (canonical_domain set). Alias
 * country-TLD sites borrow a different-language canonical's config and signal
 * their real locale only via <html lang>, so language detection trusts that
 * attribute for them but not for own-config sites. Defaults to false when the
 * resolver is unavailable.
 */
export function isAliasDomain(domain: string): boolean {
    return getResolverForDomain(domain)?.isAlias ?? false;
}

// legacy_api_endpoint has no unified-config equivalent.
export function hasLegacyEndpoint(_domain: string): boolean {
    return false;
}

export function getLegacyEndpoint(_domain: string): string | undefined {
    return undefined;
}

export function hasEvaluationEndpoint(domain: string): boolean {
    return Boolean(getResolverForDomain(domain)?.system.evaluation_endpoint);
}

export function getEvaluationEndpoint(domain: string): string | undefined {
    return getResolverForDomain(domain)?.system.evaluation_endpoint ?? undefined;
}

export function hasConversationEndpoint(domain: string): boolean {
    return Boolean(getResolverForDomain(domain)?.system.conversation_endpoint);
}

export function getConversationEndpoint(domain: string): string | undefined {
    return getResolverForDomain(domain)?.system.conversation_endpoint ?? undefined;
}

export function getModel(domain: string): string | undefined {
    return getResolverForDomain(domain)?.system.model?.name ?? undefined;
}

export function isInlineCTAOnDemoEnabled(domain: string): boolean {
    const ctasConfig = getResolverForDomain(domain)?.ctas;
    if (!ctasConfig) {
        return false;
    }

    switch (ctasConfig.inline_cta_on_demo_mode) {
        case 'always':
            return true;
        case 'never':
            return false;
        case 'auto':
        case undefined:
            break;
    }

    if (ctasConfig.inline_cta_on_demo === false) {
        return false;
    }

    return (ctasConfig.ctas ?? []).filter((cta) => !cta.show_on_intents?.length).length === 1;
}

export function getSiteCTAs(domain: string): CTA[] {
    const resolver = getResolverForDomain(domain);
    if (!resolver) {
        return [];
    }
    return transformCtas(resolver);
}

/**
 * CTAs for the search-bar strip: the first 3 regular CTAs, plus the intent-gated
 * CTAs matching the intent of the latest answer.
 */
export function selectStripCTAs(ctas: CTA[], intent: string | null | undefined): CTA[] {
    const regular = ctas.filter((cta) => !cta.show_on_intents?.length).slice(0, 3);
    const matched = intent ? ctas.filter((cta) => cta.show_on_intents?.includes(intent)) : [];
    return [...regular, ...matched];
}

export function getCTAByPlaceholder(domain: string, placeholder: string): CTA | undefined {
    return getSiteCTAs(domain).find((cta) => cta.placeholder === placeholder);
}

export function getCTAUrl(
    domain: string,
    placeholder: string,
    language: string = 'en'
): string | undefined {
    return findBestLocaleValue(getCTAByPlaceholder(domain, placeholder)?.langs, language)?.url;
}

export function getCTAText(
    domain: string,
    placeholder: string,
    language: string = 'en'
): string | undefined {
    return findBestLocaleValue(getCTAByPlaceholder(domain, placeholder)?.langs, language)?.text;
}

function extractCTADestinations(domain: string): string[] {
    const destinations = new Set<string>();

    for (const cta of getSiteCTAs(domain)) {
        for (const langConfig of Object.values(cta.langs ?? {})) {
            if (langConfig.url) {
                destinations.add(langConfig.url);
            }
        }
    }

    return Array.from(destinations);
}

// additional_cta_destinations does not exist in the unified config schemas.
function getAdditionalCTADestinations(_domain: string): string[] {
    return [];
}

export function getAdditionalFormTrackingPages(domain: string): string[] {
    return getResolverForDomain(domain)?.analytics.form_tracking_pages ?? [];
}

export function getThankYouPagePatterns(domain: string): string[] {
    return getResolverForDomain(domain)?.analytics.thank_you_pages ?? [];
}

function getAllCTADestinations(domain: string): string[] {
    const ctaDestinations = extractCTADestinations(domain);
    const additionalDestinations = getAdditionalCTADestinations(domain);
    const formTrackingPages = getAdditionalFormTrackingPages(domain);

    return Array.from(
        new Set([...ctaDestinations, ...additionalDestinations, ...formTrackingPages])
    );
}

export function isCurrentPageCTADestination(domain: string, currentUrl?: string): boolean {
    const url = currentUrl || (typeof window !== 'undefined' ? window.location.href : '');
    if (!url) return false;

    return getAllCTADestinations(domain).some((destination) => {
        if (destination.includes('*')) {
            return matchesUrlPattern(url, destination);
        }

        try {
            const destinationUrl = new URL(destination, url);
            const currentUrlObject = new URL(url);

            return (
                currentUrlObject.origin === destinationUrl.origin &&
                currentUrlObject.pathname.startsWith(destinationUrl.pathname)
            );
        } catch {
            return url.includes(destination);
        }
    });
}

export function getDynamicQuestionCount(domain: string): number | undefined {
    const dq = getResolverForDomain(domain)?.engagement?.dynamic_questions;
    if (dq?.enabled === false) return 0;
    return (
        dq?.number_of_questions ??
        getCurrentResolver()?.engagement?.dynamic_questions?.number_of_questions
    );
}

export function getDynamicQuestions(
    domain: string,
    language: string = 'en',
    currentUrl?: string
): string[] {
    const questionsConfig = getDynamicQuestionsConfig(domain, language);
    const companyName = getSiteNameFromContext(domain);
    const resolveTemplates = (questions: string[]): string[] =>
        companyName
            ? questions.map((question) => question.replace(/\{\{company_name\}\}/g, companyName))
            : questions;

    if (!questionsConfig) {
        return [];
    }

    if (!currentUrl) {
        if (Array.isArray(questionsConfig)) {
            return resolveTemplates(questionsConfig);
        }
        return resolveTemplates(questionsConfig.default ?? []);
    }

    if (Array.isArray(questionsConfig)) {
        return resolveTemplates(questionsConfig);
    }

    return resolveTemplates(resolveDynamicQuestionsForUrl(questionsConfig, currentUrl));
}

export function getCurrentDynamicQuestion(
    domain: string,
    language: string = 'en',
    rotationHours: number = 8
): string | null {
    const questions = getDynamicQuestions(domain, language);
    if (questions.length === 0) {
        return null;
    }

    const currentIndex =
        Math.floor(Date.now() / (rotationHours * 60 * 60 * 1000)) % questions.length;
    return questions[currentIndex];
}

export function hasDynamicQuestions(domain: string, language: string = 'en'): boolean {
    return getDynamicQuestions(domain, language).length > 0;
}

export function getSessionUtmParameterName(domain: string): string | null {
    return getResolverForDomain(domain)?.analytics.session_utm_name ?? null;
}

export function getVisitorUtmParameterName(domain: string): string | null {
    return getResolverForDomain(domain)?.analytics.visitor_utm_name ?? null;
}

// Decoration = adding visitor/session IDs to CTA links (URL attribution). Its own
// toggle, independent of the count toggle below.
export function isCtaLinkAttributionEnabled(domain: string): boolean {
    return getResolverForDomain(domain)?.ctas?.cta_link_attribution_enabled ?? false;
}

// Counting CTA link clicks. Decoration can't happen without observing the click,
// so decoration implies counting — treat either flag as "count".
export function isCtaClickTrackingEnabled(domain: string): boolean {
    return (
        (getResolverForDomain(domain)?.ctas?.cta_click_tracking_enabled ?? false) ||
        isCtaLinkAttributionEnabled(domain)
    );
}

export function isInPlaceUrlAttributionEnabled(domain: string): boolean {
    return getResolverForDomain(domain)?.ctas?.in_place_url_attribution_enabled ?? false;
}

export function getAdditionalCtaLinkAttributionUrls(domain: string): string[] {
    const additionalUrls = getResolverForDomain(domain)?.ctas?.additional_cta_urls ?? [];
    const urls = new Set<string>();

    for (const cta of additionalUrls) {
        for (const url of Object.values(cta.url ?? {})) {
            const trimmed = url.trim();
            if (trimmed) {
                urls.add(trimmed);
            }
        }
    }

    return Array.from(urls);
}

/**
 * IX-3617: CSS selectors for CTA buttons that are NOT links (Webflow / JS buttons
 * with no href), so click tracking can match them by element instead of destination.
 */
export function getCtaClickSelectors(
    domain: string
): Array<{ selector: string; texts: string[]; label?: string }> {
    const selectors = getResolverForDomain(domain)?.ctas?.cta_click_selectors ?? [];
    return selectors
        .filter((s) => Boolean(s?.selector?.trim()))
        .map((s) => ({
            selector: s.selector.trim(),
            // Per-language button texts to narrow the selector. Empty ⇒ selector-only.
            texts: (s.text ?? []).map((t) => t.trim()).filter(Boolean),
            label: s.label?.trim() || undefined,
        }));
}

export function getCtaParametersForwardingMode(domain: string): CtaParametersForwardingMode {
    const mode = getResolverForDomain(domain)?.ctas?.parameters_forwarding as string | undefined;
    if (mode === 'ads_only') {
        return 'allowlist';
    }
    if (mode === 'none' || mode === 'allowlist' || mode === 'all') {
        return mode;
    }
    return 'allowlist';
}

export function getCtaParametersForwardingAllowlist(domain: string): string[] {
    const allowlist =
        getResolverForDomain(domain)?.ctas?.parameters_forwarding_allowlist ??
        DEFAULT_CTA_PARAMETERS_FORWARDING_ALLOWLIST;
    const normalized = new Set<string>();

    for (const key of allowlist) {
        const trimmed = key.trim();
        if (trimmed) {
            normalized.add(trimmed);
        }
    }

    return Array.from(normalized);
}

export function isEvaluationSupportedByMode(domain: string): boolean {
    const system = getResolverForDomain(domain)?.system;
    return Boolean(system?.evaluation_endpoint || system?.api_endpoint);
}

export function hasLanguageSpecificDemoURLs(domain: string): boolean {
    const demoCta = getCTAByPlaceholder(domain, '<<DEMO-BUTTON>>');
    return Boolean(demoCta && Object.keys(demoCta.langs).length > 1);
}

export function getLanguageSpecificDemoURLs(domain: string): Record<string, string> | undefined {
    const demoCta = getCTAByPlaceholder(domain, '<<DEMO-BUTTON>>');
    if (!demoCta) {
        return undefined;
    }

    const urls = Object.fromEntries(
        Object.entries(demoCta.langs)
            .filter(([, value]) => Boolean(value.url))
            .map(([lang, value]) => [lang, value.url])
    );

    return Object.keys(urls).length > 0 ? urls : undefined;
}

export function getLanguageSpecificButtonTexts(domain: string): Record<string, string> | undefined {
    const demoCta = getCTAByPlaceholder(domain, '<<DEMO-BUTTON>>');
    if (!demoCta) {
        return undefined;
    }

    const texts = Object.fromEntries(
        Object.entries(demoCta.langs)
            .filter(([, value]) => Boolean(value.text))
            .map(([lang, value]) => [lang, value.text])
    );

    return Object.keys(texts).length > 0 ? texts : undefined;
}

export function getWidgetDisplayMode(domain: string): 'normal' | 'hidden_zone_click' {
    return getResolverForDomain(domain)?.traffic_control.widget_display_mode ?? 'hidden_zone_click';
}

export function getPageDisplayRules(domain: string): PageDisplayRule[] {
    return getResolverForDomain(domain)?.traffic_control.page_display_rules ?? [];
}

export function shouldAlwaysShowDisclaimer(domain: string): boolean {
    return getResolverForDomain(domain)?.appearance.disclaimers?.always_show === true;
}

export function getCustomDisclaimers(domain: string) {
    return getResolverForDomain(domain)?.appearance.disclaimers?.text;
}

export function getZIndex(domain: string): number {
    return getResolverForDomain(domain)?.appearance.z_index ?? 9999;
}

export function getFormDetectionStrategies(domain: string): {
    superform: boolean;
    hubspot: boolean;
    cta_page_form: boolean;
    thankyou_page: boolean;
    network_observer: boolean;
    dom_observer: boolean;
    dom_observer_track_wrapped_forms: boolean;
    post_message: boolean;
    data_layer: boolean;
} {
    const strategies = getResolverForDomain(domain)?.analytics.form_detection_strategies;
    return {
        superform: strategies?.superform !== false,
        hubspot: strategies?.hubspot !== false,
        cta_page_form: strategies?.cta_page_form !== false,
        thankyou_page: strategies?.thankyou_page !== false,
        network_observer: strategies?.network_observer !== false,
        dom_observer: strategies?.dom_observer === true, // disabled by default
        dom_observer_track_wrapped_forms: strategies?.dom_observer_track_wrapped_forms === true, // disabled by default
        post_message: strategies?.post_message !== false,
        data_layer: strategies?.data_layer !== false,
    };
}

export function getDataLayerConversionEvents(domain: string): DataLayerConversionEvent[] {
    const events = getResolverForDomain(domain)?.analytics.dataLayer_conversion_events;
    if (!Array.isArray(events)) return [];
    // Schema codegen widens to `unknown[]` because the JSON Schema uses oneOf;
    // narrowing happens inside DataLayerStrategy.normalizeEntry, so just pass
    // the array through.
    return events as DataLayerConversionEvent[];
}

export function getPostRequestCaptureConfig(
    domain: string
): { enabled?: boolean; redactPayloadValues?: boolean; pagePatterns?: string[] } | null {
    const postRequestCapture = getResolverForDomain(domain)?.analytics.post_request_capture;
    if (!postRequestCapture) {
        return null;
    }

    return {
        enabled: postRequestCapture.enabled,
        redactPayloadValues: postRequestCapture.redact_payload_values !== false,
        pagePatterns: postRequestCapture.page_patterns,
    };
}

export function isCurrentPageDemoURL(domain: string): boolean {
    const ctas = getSiteCTAs(domain);

    logger.debug('🎯 isCurrentPageDemoURL check', {
        domain,
        hasCtas: ctas.length > 0,
        ctasCount: ctas.length,
    });

    if (ctas.length === 0) {
        logger.debug('🎯 isCurrentPageDemoURL: false (no CTAs configured)');
        return false;
    }

    const currentURL = window.location.href;
    const currentPathname = window.location.pathname;

    for (const cta of ctas) {
        for (const [lang, langConfig] of Object.entries(cta.langs)) {
            const ctaURL = langConfig?.url;
            if (!ctaURL) continue;

            try {
                if (ctaURL.startsWith('/')) {
                    if (currentPathname === ctaURL || currentPathname.startsWith(`${ctaURL}/`)) {
                        logger.debug('🎯 Current page matches CTA destination (relative)', {
                            currentPathname,
                            ctaURL,
                            placeholder: cta.placeholder,
                            lang,
                        });
                        return true;
                    }
                } else {
                    const ctaUrlObj = new URL(ctaURL);
                    const currentUrlObj = new URL(currentURL);
                    const normalizedCtaPath = ctaUrlObj.pathname.replace(/\/+$/, '') || '/';
                    const normalizedCurrentPath = currentPathname.replace(/\/+$/, '') || '/';
                    const sameOrigin = currentUrlObj.origin === ctaUrlObj.origin;
                    const pathMatches =
                        normalizedCurrentPath === normalizedCtaPath ||
                        normalizedCurrentPath.startsWith(`${normalizedCtaPath}/`);

                    if (sameOrigin && pathMatches) {
                        logger.debug('🎯 Current page matches CTA destination (absolute)', {
                            currentURL,
                            currentPathname: normalizedCurrentPath,
                            ctaURL,
                            ctaPathname: normalizedCtaPath,
                            placeholder: cta.placeholder,
                            lang,
                        });
                        return true;
                    }
                }
            } catch (error) {
                logger.warn('Failed to parse CTA URL:', ctaURL, error);
            }
        }
    }

    return false;
}

export function shouldEnableFormTracking(domain: string): boolean {
    const currentUrl = window.location.href;
    const additionalPatterns = getAdditionalFormTrackingPages(domain);
    const thankYouPatterns = getThankYouPagePatterns(domain);

    logger.debug('📋 shouldEnableFormTracking check', {
        domain,
        currentUrl,
        additionalPatterns,
        thankYouPatterns,
    });

    if (isCurrentPageDemoURL(domain)) {
        return true;
    }

    if (additionalPatterns.some((pattern) => matchesUrlPattern(currentUrl, pattern))) {
        return true;
    }

    if (thankYouPatterns.some((pattern) => matchesUrlPattern(currentUrl, pattern))) {
        return true;
    }

    logger.debug('📋 shouldEnableFormTracking: false (no matches)');
    return false;
}

/**
 * Conversion filter gate, called from `trackFormSubmissionIfAllowed`.
 *
 * Evaluates the four `analytics.conversion_filters` axes against a submission:
 * - `form_id_whitelist` / `form_action_whitelist`: exact match against the
 *   form's HTML id / action URL.
 * - `url_include`: the page URL must contain at least one fragment.
 * - `url_exclude`: the page URL must contain none of the fragments.
 *
 * Each list is AND-ed with the others; within a list any match wins (OR). An
 * empty/unset list means "this axis doesn't apply", so a fully-empty
 * `conversion_filters` is passthrough (track all forms) and current behaviour
 * is preserved for unmigrated clients.
 *
 * Every axis is also skipped when the submission carries no value for it: a
 * detection with no form id is not "a form id that fails the whitelist", it is
 * a detection the whitelist cannot speak about. This mirrors the server
 * (`passes_conversion_filter`, transforms.py), which guards each axis on
 * `is not None`. IX-4341 worked around the missing guard here with a
 * `urlAxesOnly` flag that disabled the whitelist axes for whole detection
 * methods; that also disabled them for post-message and dataLayer detections
 * that DO carry a usable form id, letting non-lead forms through as
 * `rw_client_form_submitted` (IX-4411).
 *
 * `applyFormIdWhitelist: false` marks a `formId` a strategy minted for dedup
 * rather than read off the submission (DataLayer's event-name fallback); the
 * form-id axis skips those for the same reason it skips a missing id.
 */
export function passesConversionFilter(domain: string, data: FormTrackingData): boolean {
    const filters = getResolverForDomain(domain)?.analytics?.conversion_filters;
    if (!filters) {
        return true;
    }

    const formIdWhitelist = filters.form_id_whitelist ?? [];
    const formActionWhitelist = filters.form_action_whitelist ?? [];
    const urlInclude = filters.url_include ?? [];
    const urlExclude = filters.url_exclude ?? [];
    const url = data.pageUrl ?? '';

    const formIdIsIdentity = data.applyFormIdWhitelist !== false;

    let allowed = true;
    if (
        formIdWhitelist.length > 0 &&
        data.formId &&
        formIdIsIdentity &&
        !formIdWhitelist.includes(data.formId)
    ) {
        allowed = false;
    } else if (
        formActionWhitelist.length > 0 &&
        data.formAction &&
        !formActionWhitelist.includes(data.formAction)
    ) {
        allowed = false;
    } else if (
        urlInclude.length > 0 &&
        url !== '' &&
        !urlInclude.some((fragment) => url.includes(fragment))
    ) {
        allowed = false;
    } else if (urlExclude.some((fragment) => url.includes(fragment))) {
        allowed = false;
    }

    logger.debug('🎚️ passesConversionFilter check', {
        domain,
        formId: data.formId,
        formAction: data.formAction,
        pageUrl: url,
        formIdWhitelist,
        formActionWhitelist,
        urlInclude,
        urlExclude,
        allowed,
    });

    return allowed;
}

export function areFollowUpSuggestionsDisabled(domain: string): boolean {
    return getResolverForDomain(domain)?.engagement?.followup_suggestions?.enabled === false;
}

export function getAgentsConfig(domain: string): AgentDefinition[] {
    const agents = getResolverForDomain(domain)?.agents?.agents;
    if (!agents) {
        return [];
    }

    return Object.entries(agents).map(([id, agent]) => ({
        id,
        name: agent.name,
        status: agent.status,
        enabled: agent.enabled,
        description: agent.description,
    }));
}

interface ParentWindowEventsConfig {
    enabled: boolean;
    targetOrigin: string;
    eventCategories: string[];
}

export function getParentWindowEventsConfig(domain: string): ParentWindowEventsConfig | null {
    const parentWindowConfig = getParentWindowEventsSettings(domain);
    if (parentWindowConfig?.enabled === false) {
        return null;
    }

    return {
        enabled: true,
        targetOrigin: parentWindowConfig?.target_origin || '*',
        eventCategories: parentWindowConfig?.event_categories || [
            'user_action',
            'conversion',
            'system',
        ],
    };
}

function getPreprodInchatBookingOverride(): boolean | null {
    try {
        const value = localStorage.getItem('rose_preprod_inchat_booking_override');
        if (value === 'true') return true;
        if (value === 'false') return false;
        return null;
    } catch {
        return null;
    }
}

export function isInchatBookingEnabled(domain: string): boolean {
    const preprodOverride = getPreprodInchatBookingOverride();
    if (preprodOverride !== null) {
        return preprodOverride;
    }

    const resolver = getResolverForDomain(domain);
    return resolver ? resolver.isFeatureEnabled('inchat_booking') : false;
}

/**
 * Booking-calendar render mode for a CTA URL, or null to navigate normally.
 *
 * The provider (Cal.com / Calendly) is detected from the URL host, then its
 * per-provider integration config decides whether the calendar is shown and how
 * ('in-chat' embed vs 'popup' overlay). Depends on `inchat_booking` (email
 * collection) being on — the calendar only runs inside that flow.
 */
export function getBookingCalendarMode(
    domain: string,
    url: string | null | undefined
): 'in-chat' | 'popup' | null {
    if (!isInchatBookingEnabled(domain)) return null;
    const provider = resolveBookingProvider(url);
    if (!provider?.configKey) return null;
    const cfg = getResolverForDomain(domain)?.integrations?.[provider.configKey];
    if (cfg?.enabled !== true) return null;
    return cfg.mode ?? 'in-chat';
}

export function isAISectionsMobileEnabled(domain?: string | null): boolean {
    const resolver = domain ? getResolverForDomain(domain) : getCurrentResolver();
    if (!resolver) {
        return true;
    }

    return resolver.ai_sections?.enable_mobile !== false;
}

export function isFormAssistantEnabled(domain?: string): boolean {
    const resolver = domain ? getResolverForDomain(domain) : getCurrentResolver();
    return resolver ? resolver.isFeatureEnabled('form_assistant') : false;
}

function patternSpecificity(
    pattern: string,
    url: string
): { rightmost: number; literalChars: number } {
    let urlToMatch = url;
    if (!pattern.includes('?') && !pattern.includes('#')) {
        try {
            const urlObj = new URL(url);
            urlToMatch = urlObj.origin + urlObj.pathname;
        } catch {
            urlToMatch = url.split('?')[0].split('#')[0];
        }
    }

    const segments = pattern.split('*').filter((segment) => segment.length > 0);
    const literalChars = segments.reduce((sum, segment) => sum + segment.length, 0);

    if (segments.length === 0) {
        return { rightmost: 0, literalChars: 0 };
    }

    let pos = 0;
    for (const segment of segments) {
        const idx = urlToMatch.indexOf(segment, pos);
        if (idx === -1) {
            return { rightmost: pos, literalChars };
        }
        pos = idx + segment.length;
    }

    return { rightmost: pos, literalChars };
}

function anyFormAssistantPageRuleMatches(domain: string, currentUrl?: string): boolean {
    const url = currentUrl || (typeof window !== 'undefined' ? window.location.href : '');
    if (!url) return false;

    const pages = getResolverForDomain(domain)?.form_assistant?.pages;
    if (!pages) return false;

    for (const rules of Object.values(pages)) {
        for (const rule of rules) {
            for (const pattern of rule.patterns) {
                if (matchesUrlPattern(url, pattern)) {
                    return true;
                }
            }
        }
    }

    return false;
}

export function getFormAssistantQuestions(
    domain: string,
    language: string = 'en',
    currentUrl?: string
): string[] {
    const url = currentUrl || (typeof window !== 'undefined' ? window.location.href : '');
    if (!url) return [];

    const pagesByLanguage = getResolverForDomain(domain)?.form_assistant?.pages;
    const langRules = findBestLocaleValue(pagesByLanguage, language);
    if (!langRules || langRules.length === 0) return [];

    let bestQuestions: string[] | null = null;
    let bestRightmost = -1;
    let bestLiteralChars = -1;

    for (const rule of langRules) {
        for (const pattern of rule.patterns) {
            if (matchesUrlPattern(url, pattern)) {
                const spec = patternSpecificity(pattern, url);
                if (
                    spec.rightmost > bestRightmost ||
                    (spec.rightmost === bestRightmost && spec.literalChars > bestLiteralChars)
                ) {
                    bestRightmost = spec.rightmost;
                    bestLiteralChars = spec.literalChars;
                    bestQuestions = rule.questions;
                }
            }
        }
    }

    return bestQuestions ? bestQuestions.slice(0, 3) : [];
}

export function shouldUseFormAssistant(domain: string): boolean {
    const formAssistantEnabled = isFormAssistantEnabled(domain);
    const isMobile = isMobileDevice();
    const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
    const hasMatchingRule = anyFormAssistantPageRuleMatches(domain, currentUrl);

    logger.debug('🤖 shouldUseFormAssistant check', {
        domain,
        formAssistantEnabled,
        isMobile,
        hasMatchingRule,
        currentUrl: currentUrl || 'N/A',
    });

    if (!formAssistantEnabled) {
        return false;
    }

    if (isMobile) {
        return false;
    }

    return hasMatchingRule;
}

export function matchPostConversionUrlPattern(path: string, pattern: string): boolean {
    if (!pattern?.trim()) return false;
    const escaped = pattern.replace(/[.+^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`^${escaped.replace(/\*/g, '.*')}$`);
    return regex.test(path);
}

export function getActivePostConversionForm(
    domain: string
): { formId: string; config: QualificationProfilingFormsValue } | null {
    const profiling = getResolverForDomain(domain)?.qualification?.profiling;
    if (!profiling?.enabled || !profiling?.forms) {
        return null;
    }

    const currentPathname = typeof window !== 'undefined' ? window.location.pathname : '';
    const currentUrl =
        typeof window !== 'undefined' ? window.location.origin + currentPathname : '';
    const clientActivation = getClientPostConversionActivation(domain);

    for (const [formId, formConfig] of Object.entries(profiling.forms)) {
        const trigger = formConfig?.trigger;
        if (trigger?.type !== 'post_conversion') continue;

        const pages = trigger?.pages ?? [];
        const pagesMatch = pages.some(
            (pattern: string) =>
                matchPostConversionUrlPattern(currentUrl, pattern) ||
                matchPostConversionUrlPattern(currentPathname, pattern)
        );

        const onFormSubmit = trigger?.on_form_submit;
        const formSubmitTriggerActive =
            onFormSubmit?.enabled === true &&
            (onFormSubmit?.pages ?? []).some(
                (pattern: string) =>
                    matchPostConversionUrlPattern(currentUrl, pattern) ||
                    matchPostConversionUrlPattern(currentPathname, pattern)
            ) &&
            isFormSubmitActivated(domain, formId);

        const clientApiTriggerActive =
            !!clientActivation && (!clientActivation.formId || clientActivation.formId === formId);

        if (!pagesMatch && !formSubmitTriggerActive && !clientApiTriggerActive) {
            continue;
        }

        if (formConfig?.preview !== false) {
            const params = new URLSearchParams(window.location.search);
            if (!params.has('rose_demo')) continue;
        }

        return { formId, config: formConfig };
    }

    return null;
}

export function shouldUsePostConversion(domain: string): boolean {
    return getActivePostConversionForm(domain) !== null;
}

export function isEnrichAllVisitorsEnabled(domain: string): boolean {
    return getResolverForDomain(domain)?.qualification?.enrich_all_visitors === true;
}

export function shouldEnableSpaHandling(domain: string, currentUrl?: string): boolean {
    const spaConfig = getResolverForDomain(domain)?.traffic_control.spa_widget_handling;
    return isSpaHandlingEnabledForConfig(spaConfig, currentUrl);
}
